import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'

type ConfigurationType = 'configuration' | 'feature-flag'

interface CustomerConfigurationValue {
  id: string
  key: string
  scopeType: string
  scopeId: string | null
  value: unknown
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

export interface DefinitionManifestEntry {
  key: string
  type: ConfigurationType
  title: string
  description: string
  category: string
}

export interface CustomerConfigurationInventoryItem {
  id: string
  key: string
  scopeId: string | null
  value: unknown
  createdAt: string
  updatedAt: string
  title: string
  description: string | null
  category: string | null
  type: ConfigurationType | null
  hasSchema: boolean
  isEditable: boolean
}

export interface CustomerConfigurationInventory {
  items: CustomerConfigurationInventoryItem[]
  featureFlags: CustomerConfigurationInventoryItem[]
  configurations: CustomerConfigurationInventoryItem[]
  systemOnly: CustomerConfigurationInventoryItem[]
  availableDefinitions: DefinitionManifestEntry[]
}

function sortBySchemaMetadata(
  left: CustomerConfigurationInventoryItem,
  right: CustomerConfigurationInventoryItem
) {
  const leftCategory = left.category ?? ''
  const rightCategory = right.category ?? ''
  const categoryComparison = leftCategory.localeCompare(rightCategory)

  if (categoryComparison !== 0) {
    return categoryComparison
  }

  const titleComparison = left.title.localeCompare(right.title)

  if (titleComparison !== 0) {
    return titleComparison
  }

  return left.key.localeCompare(right.key)
}

function sortByKey(
  left: CustomerConfigurationInventoryItem,
  right: CustomerConfigurationInventoryItem
) {
  return left.key.localeCompare(right.key)
}

function buildInventory(
  values: CustomerConfigurationValue[],
  definitions: DefinitionManifestEntry[]
): CustomerConfigurationInventory {
  const definitionByKey = new Map(
    definitions.map((definition) => [definition.key, definition])
  )

  const items = values.map<CustomerConfigurationInventoryItem>((value) => {
    const definition = definitionByKey.get(value.key)

    return {
      id: value.id,
      key: value.key,
      scopeId: value.scopeId,
      value: value.value,
      createdAt: value.createdAt,
      updatedAt: value.updatedAt,
      title: definition?.title ?? value.key,
      description: definition?.description ?? null,
      category: definition?.category ?? null,
      type: definition?.type ?? null,
      hasSchema: definition !== undefined,
      isEditable: definition !== undefined,
    }
  })

  const featureFlags = items
    .filter((item) => item.type === 'feature-flag')
    .sort(sortBySchemaMetadata)
  const configurations = items
    .filter((item) => item.type === 'configuration')
    .sort(sortBySchemaMetadata)
  const systemOnly = items
    .filter((item) => item.hasSchema === false)
    .sort(sortByKey)

  const existingKeys = new Set(values.map((v) => v.key))
  const availableDefinitions = definitions.filter(
    (definition) => !existingKeys.has(definition.key)
  )

  return {
    items: [...featureFlags, ...configurations, ...systemOnly],
    featureFlags,
    configurations,
    systemOnly,
    availableDefinitions,
  }
}

export default function useCustomerConfigurationInventory(customerId: string) {
  return useQuery({
    queryKey: ['customerConfigurationInventory', customerId],
    queryFn: async (): Promise<CustomerConfigurationInventory> => {
      const [valuesResponse, definitionsResponse] = await Promise.all([
        epsClient.get<CustomerConfigurationValue[]>(
          `/customers/${customerId}/configurations`
        ),
        epsClient.get<DefinitionManifestEntry[]>(`/admin/configs/definitions`),
      ])

      return buildInventory(
        valuesResponse.data ?? [],
        definitionsResponse.data ?? []
      )
    },
    enabled: customerId.length > 0,
  })
}
